"use client";

import { IconCheckOutline18, IconChevronExpandYOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { TokenIcon } from "@/components/ui/token-icon";
import { cn } from "@/lib/utils";
import * as React from "react";

import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

const QUOTE_CURRENCIES = ["USDT", "USDC", "EUR", "BTC", "ETH", "USD"];

const QUOTE_CURRENCY_SYMBOLS: Record<string, string> = {
  USDT: "$",
  USDC: "$",
  USD: "$",
  EUR: "€",
  BTC: "₿",
  ETH: "Ξ",
};

function getQuoteCurrencySymbol(symbol: string): string {
  for (const quote of QUOTE_CURRENCIES) {
    if (symbol.endsWith(quote) && symbol.length > quote.length) {
      return QUOTE_CURRENCY_SYMBOLS[quote] ?? "";
    }
  }
  return "$";
}

export function formatAssetLabel(symbol: string): string {
  if (symbol.startsWith("HL_") && symbol.endsWith("_CURRENT_FUNDING")) {
    return `${symbol.slice(3, -16)} Funding Rate`;
  }
  if (symbol.startsWith("PM_") && symbol.endsWith("_PY")) {
    return `Polymarket #${symbol.slice(3, -3)}`;
  }
  if (symbol.endsWith("_RR")) {
    return `${symbol.slice(0, -3)} (RR)`;
  }
  const twapMatch = symbol.match(/^(.+)(USD[TC]?)TWAP\d+$/);
  if (twapMatch) {
    return `${twapMatch[1]} / ${twapMatch[2]} (TWAP)`;
  }
  const equity245QuoteMatch = symbol.match(/^(.+)_24_5_([A-Z]+)$/);
  if (equity245QuoteMatch) {
    return `${equity245QuoteMatch[1]} (24/5, ${equity245QuoteMatch[2]})`;
  }
  if (symbol.endsWith("_24_5")) {
    return `${symbol.slice(0, -5)} (24/5)`;
  }
  if (symbol.includes("_")) {
    return symbol.replace(/_/g, " ");
  }
  if (symbol.endsWith("MARK") && symbol.length > 4) {
    const base = symbol.slice(0, -4);
    for (const quote of QUOTE_CURRENCIES) {
      if (base.endsWith(quote) && base.length > quote.length) {
        return `${base.slice(0, -quote.length)} / ${quote} (Mark)`;
      }
    }
  }
  for (const quote of QUOTE_CURRENCIES) {
    if (symbol.endsWith(quote) && symbol.length > quote.length) {
      return `${symbol.slice(0, -quote.length)} / ${quote}`;
    }
  }
  return symbol;
}

function getAssetTooltip(symbol: string): string | null {
  if (symbol.startsWith("HL_") && symbol.endsWith("_CURRENT_FUNDING")) return "Hyperliquid perpetual funding rate";
  if (symbol.startsWith("PM_") && symbol.endsWith("_PY")) return "Polymarket prediction market outcome price";
  if (symbol.endsWith("_RR")) return "Reserve Ratio — exchange rate between two tokens";
  if (/TWAP\d+$/.test(symbol)) return "Time-Weighted Average Price over a rolling window";
  if (/_24_5/.test(symbol)) return "US equity with 24-hour, 5-day extended trading hours";
  if (symbol.endsWith("MARK")) return "Mark Price — fair value for perpetual futures";
  return null;
}

interface AvailableAsset {
  symbol: string;
}

interface AssetSelectorProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  popoverModal?: boolean;
}

interface AssetMeta {
  label: string;
  tooltip: string | null;
}

const AssetLabel = React.memo(function AssetLabel({ symbol, meta }: { symbol: string; meta: AssetMeta | undefined }) {
  const formattedLabel = meta?.label ?? symbol;
  const tooltip = meta?.tooltip ?? null;
  const content = (
    <span className="flex-1" {...(tooltip ? { tabIndex: 0 } : {})}>
      <span className="font-medium">{formattedLabel}</span>
      <span className="text-ds-text-muted ml-2 text-xs">{symbol}</span>
    </span>
  );
  if (!tooltip) return content;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{content}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-60 text-xs">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
});

function AssetSelector({
  label,
  description,
  headerRight,
  value,
  onChange,
  required = false,
  popoverModal = false,
}: AssetSelectorProps) {
  const [containerEl, setContainerEl] = React.useState<HTMLDivElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [assets, setAssets] = React.useState<AvailableAsset[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    const fetchAssets = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${V2_ENDPOINT}/v1/token-price-cexes/available-assets`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const responseData = await response.json();
          const assetStrings = responseData?.data?.assets || [];
          const assetsArray: AvailableAsset[] = Array.isArray(assetStrings)
            ? assetStrings.map((symbol: string) => ({ symbol }))
            : [];
          setAssets(assetsArray);
        } else {
          setAssets([]);
        }
      } catch (error) {
        if ((error as Error).name === "AbortError") return;
        console.error("[AssetSelector] Failed to fetch available assets:", error);
        setAssets([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAssets();
    return () => controller.abort();
  }, []);

  const assetMeta = React.useMemo(
    () =>
      new Map(assets.map(a => [a.symbol, { label: formatAssetLabel(a.symbol), tooltip: getAssetTooltip(a.symbol) }])),
    [assets],
  );

  const selectedAsset = React.useMemo(() => assets.find(a => a.symbol === value) ?? null, [assets, value]);

  const [currentPrice, setCurrentPrice] = React.useState<number | null>(null);
  React.useEffect(() => {
    if (!value) {
      setCurrentPrice(null);
      return;
    }
    setCurrentPrice(null);
    let cancelled = false;
    const controller = new AbortController();
    authenticatedFetch<{ data: { prices: Record<string, string> } }>(
      `${V2_ENDPOINT}/v1/token-price-cexes/latest?assets=${encodeURIComponent(value)}`,
      { signal: controller.signal },
    )
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled) return;
        const priceStr = data?.data?.prices?.[value];
        setCurrentPrice(priceStr ? parseFloat(priceStr) : null);
      })
      .catch(() => {
        if (!cancelled) setCurrentPrice(null);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [value]);

  const filteredAssets = React.useMemo(() => {
    if (!searchQuery) return assets;
    const query = searchQuery.toLowerCase();
    return assets.filter(
      asset =>
        asset.symbol.toLowerCase().includes(query) ||
        (assetMeta.get(asset.symbol)?.label.toLowerCase().includes(query) ?? false),
    );
  }, [assets, searchQuery, assetMeta]);

  const handleSelect = (asset: AvailableAsset) => {
    onChange(asset.symbol);
    setOpen(false);
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div ref={setContainerEl} className="space-y-2">
        {label && (
          <div className="flex items-center justify-between gap-3">
            <label className="field-label flex items-center gap-2">
              <span>{label}</span>
              {required && <span className="ml-1 text-red-500">*</span>}
            </label>
            {headerRight}
          </div>
        )}
        {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        <Popover
          open={open}
          onOpenChange={nextOpen => {
            setOpen(nextOpen);
            if (!nextOpen) setSearchQuery("");
          }}
          modal={popoverModal}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              role="combobox"
              aria-expanded={open}
              className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            >
              {selectedAsset ? (
                <span className="flex items-center gap-2">
                  <TokenIcon symbol={selectedAsset.symbol} size={20} enableStockTickers />
                  <AssetLabel symbol={selectedAsset.symbol} meta={assetMeta.get(selectedAsset.symbol)} />
                </span>
              ) : (
                <span className="text-ds-text-disabled dark:text-ds-text-muted">Select asset...</span>
              )}
              <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
            side="bottom"
            container={popoverModal ? (containerEl ?? undefined) : undefined}
          >
            <Command shouldFilter={false}>
              <CommandInput placeholder="Search assets..." value={searchQuery} onValueChange={setSearchQuery} />
              <CommandList className="max-h-64">
                {isLoading ? (
                  <div className="body-sm py-6 text-center">Loading assets...</div>
                ) : (
                  <>
                    <CommandEmpty>No asset found.</CommandEmpty>
                    <CommandGroup>
                      {filteredAssets.map(asset => (
                        <CommandItem
                          key={asset.symbol}
                          value={asset.symbol}
                          onSelect={() => handleSelect(asset)}
                          className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800"
                        >
                          <div className="flex w-full cursor-pointer items-center gap-2">
                            <IconCheckOutline18
                              className={cn(
                                "h-4 w-4 flex-shrink-0",
                                value === asset.symbol ? "opacity-100" : "opacity-0",
                              )}
                            />
                            <TokenIcon symbol={asset.symbol} size={20} enableStockTickers />
                            <AssetLabel symbol={asset.symbol} meta={assetMeta.get(asset.symbol)} />
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedAsset && currentPrice !== null && (
          <p className="text-ds-text-subtle text-xs">
            Currently trading at{" "}
            <span className="text-ds-text-primary font-medium">
              {getQuoteCurrencySymbol(selectedAsset.symbol)}
              {currentPrice >= 1
                ? currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
            </span>
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}

export default React.memo(AssetSelector);
